import { AxesHelper, Bone, Box3, BoxGeometry, Camera, CameraHelper, Controls, DirectionalLight, DirectionalLightHelper, Intersection, IUniform, Light, Line, Loader, LoadingManager, Material, Matrix4, Mesh, MeshBasicMaterial, Object3D, PointLight, PointLightHelper, Points, Quaternion, Ray, Raycaster, Scene, SkeletonHelper, SkinnedMesh, SphereGeometry, Sprite, SpotLightHelper, Texture, TextureLoader, UniformsGroup, UniformsLib, Vector2, Vector3, WebGLRenderer } from "three";
import { ILogService } from "../../log/common/log";
import { NodeFlags } from "../../hierarchy/common/hierarchy";
import { isHideInHierarchy } from "../../hierarchy/common/hierarchyService";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module";
import { GLTFLike } from "../common/gltf";
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader.js";
import { URI } from "../../../base/common/uri";
import { CancellationToken } from "../../../base/common/cancellation";
import { isColorLike } from "../../properties/common/types";
import { markEditorThreePrototype } from "./editorThreeOwnership.js";

// The page hook must not report objects created by the editor's bundled Three runtime.
markEditorThreePrototype(Object3D);
markEditorThreePrototype(WebGLRenderer);
markEditorThreePrototype(Loader);


// #region Three.js Version

// Extract Three.js version number from build-time constant
// __THREE_VERSION__ is injected by Vite at build time via define
// Note: Three.js uses 0.x.x versioning, so we use the minor version (second number)
// e.g., "0.169.10" → 169, "0.182.0" → 182
export const THREE_MAJOR_VERSION: number = (() => {
    try {
        // @ts-ignore - __THREE_VERSION__ is defined by Vite
        const version = __THREE_VERSION__;
        if (version && typeof version === 'string') {
            const parts = version.split('.');
            // Three.js is 0.x.x, so use the minor version (index 1)
            const minorVersion = parseInt(parts[1]);
            return isNaN(minorVersion) ? -1 : minorVersion;
        }
    } catch (e) {
        // __THREE_VERSION__ not defined - fallback to -1
    }
    return -1;
})();


// #region Cross-version three.js compatibility
// The inspector bundles its own three.js, but the helper objects we create
// (light/camera/skeleton helpers, selection wireframes, bounding-box lines) are
// added into the *page's* scene graph and drawn by the *page's* renderer. When
// the page runs a newer three.js than we bundle, its renderer calls methods on
// our objects' matrices that our (older) three.js doesn't have — e.g.
// `matrixWorld.determinantAffine()` (three r185+ WebGLRenderer.renderBufferDirect),
// which otherwise throws "determinantAffine is not a function" as soon as a
// helper is added to the scene (e.g. selecting a light). Provide the method on
// our Matrix4 so our objects render under a newer host renderer. For affine
// matrices the affine determinant is the upper-left 3x3 determinant, which is
// exactly what three computes.
if (typeof (Matrix4.prototype as unknown as { determinantAffine?: unknown }).determinantAffine !== "function") {
    (Matrix4.prototype as unknown as { determinantAffine: () => number }).determinantAffine = function (this: Matrix4): number {
        const te = this.elements;
        const n11 = te[0], n21 = te[1], n31 = te[2];
        const n12 = te[4], n22 = te[5], n32 = te[6];
        const n13 = te[8], n23 = te[9], n33 = te[10];
        return n11 * (n22 * n33 - n32 * n23)
            - n12 * (n21 * n33 - n31 * n23)
            + n13 * (n21 * n32 - n31 * n22);
    };
}

// r186 moved the renderer's culling call from Frustum.intersectsObject(object)
// to object.intersectsFrustum(frustum). Models parsed by our r185 bundle still
// enter a newer host renderer, so install the r186 methods with the exact legacy
// culling behavior they replaced.
type FrustumCompatibility = {
    intersectsObject(object: Object3D): boolean;
    intersectsSprite(object: Sprite): boolean;
};
const installObjectFrustumTest = (prototype: object) => {
    const compatible = prototype as { intersectsFrustum?: (frustum: FrustumCompatibility) => boolean };
    if (typeof compatible.intersectsFrustum !== "function") {
        compatible.intersectsFrustum = function (this: Object3D, frustum: FrustumCompatibility): boolean {
            return frustum.intersectsObject(this);
        };
    }
};
installObjectFrustumTest(Mesh.prototype);
installObjectFrustumTest(Line.prototype);
installObjectFrustumTest(Points.prototype);
const spritePrototype = Sprite.prototype as unknown as { intersectsFrustum?: (frustum: FrustumCompatibility) => boolean };
if (typeof spritePrototype.intersectsFrustum !== "function") {
    spritePrototype.intersectsFrustum = function (this: Sprite, frustum: FrustumCompatibility): boolean {
        return frustum.intersectsSprite(this);
    };
}
// #endregion


// #region KTX2 basis transcoder path
const CDN_BASIS_TRANSCODER_PATH = "https://cdn.needle.tools/static/three/0.179.1/basis2/";

/**
 * Base path (must end with "/") from which KTX2Loader loads basis_transcoder.js/.wasm.
 *
 * In the Chrome extension the transcoder is shipped inside the extension bundle
 * (dependencies/basis2/) so it loads instantly instead of from the CDN, which can take
 * many seconds. The core bundle runs in the page's MAIN world where `chrome.runtime` is not
 * available, so we derive the extension URL from the module's own URL — `import.meta.url`
 * resolves to `chrome-extension://<id>/dependencies/editor.js` at runtime. Falls back to the
 * CDN for the PWA/web build or if the extension URL can't be determined.
 */
export function getBasisTranscoderPath(): string {
    try {
        // @ts-ignore - __BUILD_TARGET__ is injected by Vite at build time via define
        if (typeof __BUILD_TARGET__ !== "undefined" && __BUILD_TARGET__ === "chrome") {
            const url = new URL("./basis2/", import.meta.url).href;
            if (url.startsWith("chrome-extension://")) return url;
        }
    } catch {
        // fall through to the CDN path
    }
    return CDN_BASIS_TRANSCODER_PATH;
}


// #region Type Guards

/** three AnimationMixer, duck-typed (no is* flag exists on it) */
export function isAnimationMixer(obj: unknown): obj is {
    time: number;
    timeScale: number;
    getRoot(): { name?: string } | undefined;
    _actions: unknown[];
} {
    if (!obj || typeof obj !== "object") return false;
    const mixer = obj as { clipAction?: unknown; update?: unknown; _actions?: unknown };
    return typeof mixer.clipAction === "function" && typeof mixer.update === "function" && Array.isArray(mixer._actions);
}

/** three AnimationAction, duck-typed (no is* flag exists on it) */
export function isAnimationActionLike(obj: unknown): obj is {
    time: number;
    loop: number;
    isRunning(): boolean;
    getClip(): { name?: string; duration?: number };
} {
    if (!obj || typeof obj !== "object") return false;
    const action = obj as { play?: unknown; getClip?: unknown; isRunning?: unknown };
    return typeof action.play === "function" && typeof action.getClip === "function" && typeof action.isRunning === "function";
}

/** three AnimationClip, duck-typed (no is* flag exists on it) */
export function isAnimationClipLike(obj: unknown): obj is {
    name: string;
    duration: number;
    tracks: unknown[];
} {
    if (!obj || typeof obj !== "object") return false;
    // `resetDuration` (public AnimationClip API) rules out look-alikes that carry
    // only tracks + duration — e.g. plain snapshots of a clip
    const clip = obj as { tracks?: unknown; duration?: unknown; resetDuration?: unknown };
    return Array.isArray(clip.tracks) && typeof clip.duration === "number" && typeof clip.resetDuration === "function";
}

/**
 * A loader RESULT wrapper (parsed gltf/collada: `{ scene, animations, asset, … }`)
 * that is not itself an Object3D (FBX/OBJ return the root directly — those go
 * through the normal Object3D path).
 */
export function isLoadedModelResult(obj: unknown): obj is {
    scene?: { isObject3D?: boolean; name?: string };
    scenes?: unknown[];
    animations?: unknown[];
    asset?: { generator?: string };
} {
    if (!obj || typeof obj !== "object") return false;
    if (isObject3D(obj)) return false;
    // TSL nodes (e.g. PassNode) carry a `.scene` Object3D too — they are nodes,
    // not loaded files; exclude them or they misclassify as a "File" model.
    if ((obj as { isNode?: boolean }).isNode === true) return false;
    const result = obj as { scene?: { isObject3D?: boolean }; scenes?: { isObject3D?: boolean }[] };
    return result.scene?.isObject3D === true
        || (Array.isArray(result.scenes) && result.scenes[0]?.isObject3D === true);
}

/**
 * A TSL shader-graph node object (revealed by clicking a node-graph card).
 * Duck-typed on the universal `isNode` flag + the `nodeType` field every TSL
 * node carries; excludes Object3D (which never sets `isNode`) defensively.
 */
export function isTSLNode(obj: unknown): obj is { isNode: true; nodeType?: string | null; type?: string; name?: string } {
    if (!obj || typeof obj !== "object") return false;
    const node = obj as { isNode?: boolean; isObject3D?: boolean };
    return node.isNode === true && "nodeType" in node && node.isObject3D !== true;
}
export function isOperatorNode(obj: unknown): obj is { isNode: true; isOperatorNode: true } {
    if (!obj || typeof obj !== "object") return false;
    const node = obj as { isNode?: boolean; isOperatorNode?: boolean };
    return node.isNode === true && node.isOperatorNode === true;
}

export function isRenderer(obj: unknown): obj is (import("three").WebGLRenderer & (
    { readonly isWebGLRenderer: true, readonly isWebGPURenderer: undefined, } |
    { readonly isWebGLRenderer: undefined, readonly isWebGPURenderer: true }
)) {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const renderer = obj as import("three").WebGLRenderer;
    return typeof renderer.domElement === "object" && typeof renderer.render === "function";
}

// https://github.com/mrdoob/three.js/blob/3ef307076213aa05265d522dbc05c56e2a870a41/src/renderers/common/Renderer.js#L109
type RendererWithBackend = {
    isRenderer: true,
    backend: {
        data: WeakMap<object, unknown>
    }
};
export function isRendererWithBackend(obj: unknown): obj is RendererWithBackend {
    if (!obj) return false;
    const renderer = obj as RendererWithBackend;
    return renderer.isRenderer === true && renderer.backend !== null && typeof renderer.backend === "object" && renderer.backend.data instanceof WeakMap;
}
export function isWebGLRenderer(obj: unknown): obj is import("three").WebGLRenderer {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const renderer = obj as import("three").WebGLRenderer;
    return (renderer as any).isWebGLRenderer === true && typeof renderer.domElement === "object" && typeof renderer.render === "function";
}
export function isWebGPURenderer(obj: unknown): obj is { isWebGPURenderer: true } & RendererWithBackend {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const renderer = obj as { isWebGPURenderer: true };
    return (renderer as any).isWebGPURenderer === true;
}

export function isScene(obj: unknown): obj is import("three").Scene {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const scene = obj as import("three").Scene;
    return scene.isScene === true && typeof scene.uuid === "string";
}
export function isCamera(obj: unknown): obj is Camera {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const camera = obj as Camera;
    return camera.isCamera === true && typeof camera.uuid === "string";
}
export function isPerspectiveCamera(obj: unknown): obj is import("three").PerspectiveCamera {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const camera = obj as import("three").PerspectiveCamera;
    return camera.isPerspectiveCamera === true && typeof camera.uuid === "string";
}
export function isObject3D(obj: unknown): obj is Object3D {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const obj3d = obj as Object3D;
    return obj3d.isObject3D === true && typeof obj3d.uuid === "string";
}
export function isMeshLike(obj: unknown): obj is Mesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as Mesh;
    return mesh.isMesh === true && typeof mesh.uuid === "string";
}
export function isSkinnedMeshLike(obj: unknown): obj is SkinnedMesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as SkinnedMesh;
    return mesh.isSkinnedMesh === true && typeof mesh.uuid === "string";
}
export function isSkeleton(obj: unknown): obj is import("three").Skeleton {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const skeleton = obj as import("three").Skeleton;
    return typeof skeleton.bones === "object" && Array.isArray(skeleton.bones);
}
export function isBone(obj: unknown): obj is Bone {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const bone = obj as Bone;
    return bone.isBone === true && typeof bone.uuid === "string";
}
export function isInstancedMeshLike(obj: unknown): obj is import("three").InstancedMesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as import("three").InstancedMesh;
    return mesh.isInstancedMesh === true && typeof mesh.uuid === "string";
}
export function isBatchedMeshlike(obj: unknown): obj is import("three").BatchedMesh {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mesh = obj as import("three").BatchedMesh;
    return mesh.isBatchedMesh === true && typeof mesh.uuid === "string";
}
export function isGeometry(obj: unknown): obj is import("three").BufferGeometry {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const geom = obj as import("three").BufferGeometry;
    return geom.isBufferGeometry === true && typeof geom.uuid === "string";
}
export function isMaterial(obj: unknown): obj is Material {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const mat = obj as Material;
    return mat.isMaterial === true && typeof mat.uuid === "string";
}
export function isShaderMaterial(obj: unknown): obj is import("three").ShaderMaterial {
    return isMaterial(obj) && (obj as import("three").ShaderMaterial).isShaderMaterial === true;
}
export function isRawShaderMaterial(obj: unknown): obj is import("three").RawShaderMaterial {
    return isMaterial(obj) && (obj as import("three").RawShaderMaterial).isRawShaderMaterial === true;
}
export function isUniformsGroup(obj: unknown): obj is UniformsGroup {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const ug = obj as UniformsGroup;
    return ug.isUniformsGroup === true && typeof ug.id === "string";
}
export function isUniformsArray(obj: unknown): obj is Array<import("three").IUniform> {
    if (!obj) return false;
    if (!Array.isArray(obj)) return false;
    const arr = obj as Array<import("three").IUniform>;
    for (const item of arr) {
        if (typeof item !== "object" || !("value" in item)) {
            return false;
        }
    }
    return true;
}


const commonUniformsTextureSlotNames = new Set<string>([
    "map",
    "alphaMap",
    "aoMap",
    "bumpMap",
    "displacementMap",
    "emissiveMap",
    "envMap",
    "lightMap",
    "metalnessMap",
    "normalMap",
    "roughnessMap",
    "specularMap",
    "gradientMap",
    "matcap",
    "lightProbeMap",
    "gradientMap",
]);
type MaterialTextureSlotOwner = Material | UniformsGroup | Array<IUniform>;
// owner: MaterialTextureSlotOwner, 
export function isLikelyMaterialTextureInputSlot(slotName: string, slotValue?: unknown): boolean | undefined {
    if (slotValue && isTexture(slotValue)) {
        return true;
    }
    if (commonUniformsTextureSlotNames.has(slotName)) {
        return true;
    }
    if (slotName.endsWith("Map")) {
        return true;
    }
    else if (slotName.startsWith("u") && (slotName.endsWith("Map") || slotName.endsWith("Tex"))) {
        return true;
    }
    return undefined;
}

export function isLikelyColorInputSlot(slotName: string, slotValue?: unknown): boolean | undefined {
    if (slotValue && isColorLike(slotValue) === true) {
        return true;
    }
    else if (slotName.endsWith("Color") ||
        slotName.endsWith("Colour") ||
        slotName.toLowerCase() === "color" ||
        slotName.toLowerCase() === "colour"
    ) {
        return true;
    }
    return undefined;
}


// #region Geometry Helper

export function isInterleavedBufferAttribute(obj: unknown): obj is import("three").InterleavedBufferAttribute {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const attr = obj as import("three").InterleavedBufferAttribute;
    return attr.isInterleavedBufferAttribute === true;
}

/*
export const StaticDrawUsage: 35044;
export const DynamicDrawUsage: 35048;
export const StreamDrawUsage: 35040;
export const StaticReadUsage: 35045;
export const DynamicReadUsage: 35049;
export const StreamReadUsage: 35041;
export const StaticCopyUsage: 35046;
export const DynamicCopyUsage: 35050;
export const StreamCopyUsage: 35042;
*/
export function geometryAttributeUsageToString(usage: number): string | undefined {
    switch (usage) {
        case 35044: return "StaticDrawUsage";
        case 35048: return "DynamicDrawUsage";
        case 35040: return "StreamDrawUsage";
        case 35045: return "StaticReadUsage";
        case 35049: return "DynamicReadUsage";
        case 35041: return "StreamReadUsage";
        case 35046: return "StaticCopyUsage";
        case 35050: return "DynamicCopyUsage";
        case 35042: return "StreamCopyUsage";
        default: return undefined;
    }
    return undefined;
}

/*
export const UnsignedByteType: 1009;
export const ByteType: 1010;
export const ShortType: 1011;
export const UnsignedShortType: 1012;
export const IntType: 1013;
export const UnsignedIntType: 1014;
export const FloatType: 1015;
export const HalfFloatType: 1016;
export const UnsignedShort4444Type: 1017;
export const UnsignedShort5551Type: 1018;
export const UnsignedInt248Type: 1020;
export const UnsignedInt5999Type: 35902;
export const UnsignedInt101111Type: 35899;

export type AttributeGPUType = typeof FloatType | typeof IntType;
*/

export function geometryAttributeGpuTypeToString(gpuType: number | undefined): string | undefined {
    switch (gpuType) {
        case 1009: return "UnsignedByteType";
        case 1010: return "ByteType";
        case 1011: return "ShortType";
        case 1012: return "UnsignedShortType";
        case 1013: return "IntType";
        case 1014: return "UnsignedIntType";
        case 1015: return "FloatType";
        case 1016: return "HalfFloatType";
        case 1017: return "UnsignedShort4444Type";
        case 1018: return "UnsignedShort5551Type";
        case 1020: return "UnsignedInt248Type";
        case 35902: return "UnsignedInt5999Type";
        case 35899: return "UnsignedInt101111Type";
        default: return undefined;
    }
}


// #region Texture Types
export function isTexture(obj: unknown): obj is import("three").Texture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").Texture;
    return tex.isTexture === true && typeof tex.uuid === "string";
}
export function isCompressedTexture(obj: unknown): obj is import("three").CompressedTexture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").CompressedTexture;
    return tex.isCompressedTexture === true && typeof tex.uuid === "string";
}
export function isDataTexture(obj: unknown): obj is import("three").DataTexture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").DataTexture;
    return tex.isDataTexture === true && typeof tex.uuid === "string";
}
export function isCubeTexture(obj: unknown): obj is import("three").CubeTexture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const tex = obj as import("three").CubeTexture;
    return tex.isCubeTexture === true && typeof tex.uuid === "string";
}
/**
 * Source pixel dimensions of a texture. three's `Texture` has NO `width`/
 * `height` — those live on the uploaded IMAGE (HTMLImageElement / ImageBitmap /
 * canvas / video / `{ width, height }` data image), on `source.data`, or (for
 * compressed textures) on the base mip level. Returns `{ width: 0, height: 0 }`
 * when nothing usable is found (e.g. not uploaded yet).
 */
export function getTextureSourceSize(texture: import("three").Texture): { width: number; height: number } {
    const candidates: Array<unknown> = [
        texture.image,
        (texture as { source?: { data?: unknown } }).source?.data,
        (texture as { mipmaps?: Array<unknown> }).mipmaps?.[0],
    ];
    for (const c of candidates) {
        if (!c || typeof c !== "object") continue;
        const o = c as { width?: number; height?: number; videoWidth?: number; videoHeight?: number };
        const width = o.videoWidth || o.width || 0;
        const height = o.videoHeight || o.height || 0;
        if (width > 0 && height > 0) return { width, height };
    }
    return { width: 0, height: 0 };
}

export function isWebGLRenderTarget(obj: unknown): obj is import("three").WebGLRenderTarget {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const rt = obj as import("three").WebGLRenderTarget;
    return rt.isWebGLRenderTarget === true && typeof rt.texture.uuid === "string";
}

/**
 * Detects QUANTIZED vertex data — the visible remnant of a compressed mesh
 * pipeline. Draco and meshopt themselves are fully decoded at load and leave
 * no trace on the BufferGeometry, but meshopt/gltfpack pipelines keep the
 * KHR_mesh_quantization storage: spatial attributes stored as (normalized)
 * integers instead of float32. Only spatial attributes count — skinIndex is
 * legitimately integer and uint8 colors are common uncompressed.
 * Returns a short label + a per-attribute detail string, or null.
 */
export function getGeometryQuantizationInfo(geometry: import("three").BufferGeometry): { label: string; detail: string } | null {
    const spatial = ["position", "normal", "tangent", "uv", "uv1", "uv2", "uv3"];
    const parts: string[] = [];
    let interleaved = false;
    for (const name of spatial) {
        const attribute = geometry.attributes?.[name] as { array?: ArrayLike<number>; normalized?: boolean; isInterleavedBufferAttribute?: boolean; data?: { array?: ArrayLike<number> } } | undefined;
        if (!attribute) continue;
        const array = attribute.array ?? attribute.data?.array;
        if (!array) continue;
        const ctor = (array as { constructor?: { name?: string } }).constructor?.name ?? "";
        const match = ctor.match(/^(U?)Int(8|16|32)Array$/i);
        if (!match || match[2] === "32") continue; // int32 indices aside, 8/16-bit spatial data = quantized
        if (attribute.isInterleavedBufferAttribute) interleaved = true;
        parts.push(`${name} ${match[1] ? "u" : ""}int${match[2]}${attribute.normalized ? " norm" : ""}`);
    }
    if (parts.length === 0) return null;
    return {
        label: "quantized",
        detail: `Quantized vertex data (KHR_mesh_quantization / meshopt-style storage)${interleaved ? ", interleaved" : ""}: ${parts.join(" · ")}. Draco/meshopt transport compression is decoded at load and not detectable here.`,
    };
}


export function isLight(obj: unknown): obj is Light {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const light = obj as Light;
    return light.isLight === true && typeof light.uuid === "string";
}
export function isLayermask(obj: unknown): obj is import("three").Layers {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const layers = obj as import("three").Layers;
    return "mask" in layers && typeof layers.mask === "number" && typeof layers.set === "function" && typeof layers.enable === "function";
}
export function isLineObject(obj: unknown): obj is import("three").Line {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const line = obj as import("three").Line;
    return line.isLine === true && typeof line.uuid === "string";
}
export function isTransformControls(obj: unknown): obj is import("three/examples/jsm/controls/TransformControls").TransformControls {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const controls = obj as import("three/examples/jsm/controls/TransformControls").TransformControls;
    if ("axis" in controls && "mode" in controls && "space" in controls) {
        return true;
    }
    return false;
}

/**
 * True for three.js `TransformControls` helper internals — the controls, its
 * `getHelper()` root, the gizmo, the plane, and anything parented under them.
 *
 * The editor must never select, highlight or CLONE these: cloning a
 * `TransformControlsPlane` yields `new TransformControlsPlane()`, whose
 * `worldPosition` (normally synced by the parent controls) is `undefined`, so
 * its `updateMatrixWorld` crashes at render with `Vector3.copy(undefined)`
 * ("Cannot read properties of undefined (reading 'x')"). Walks ancestors so the
 * whole gizmo subtree is excluded, not just the root object.
 */
export function isTransformControlsArtifact(obj: unknown): boolean {
    let current = obj as { type?: unknown; parent?: unknown; isTransformControls?: unknown } | null | undefined;
    while (current && typeof current === "object") {
        const type = typeof current.type === "string" ? current.type : "";
        if (current.isTransformControls === true || type.startsWith("TransformControls")) {
            return true;
        }
        current = current.parent as typeof current;
    }
    return false;
}

export type ObjectWithMaterial = Object3D & { material: Material | Material[] };

export function isObjectWithMaterials(object: Object3D): object is ObjectWithMaterial {
    return !!(object as any).material;
}

export function safeClone(obj: ObjectWithMaterial, logger: ILogService): ObjectWithMaterial | null {

    let clone: ObjectWithMaterial | null = null;

    try {
        switch (obj.type) {
            case "Mesh":
                // case "SkinnedMesh":
                const mesh = obj as Mesh | SkinnedMesh;

                // make sure the object has a valid geometry assignment
                if (!mesh.geometry || !isGeometry(mesh.geometry)) {
                    // Is this a Spline object? 
                    if ('geometryCreateDeleyed' in mesh) {
                        if (typeof mesh.geometryCreateDeleyed === "function") {
                            mesh.geometryCreateDeleyed();
                        }
                        else {
                            mesh.geometry = mesh.geometryCreateDeleyed as any;
                        }
                    }
                    if (!isGeometry(mesh.geometry)) {
                        logger.warn(`Mesh ${mesh.name || "unnamed"} has no geometry assigned, cannot clone properly.`);
                        return null;
                    }
                }

                clone = new (Object.getPrototypeOf(obj).constructor)(mesh.geometry, mesh.material);
                const clonedMesh = clone as Mesh;
                clonedMesh.geometry = mesh.geometry;
                clonedMesh.material = mesh.material;
                break;

            // case "SkinnedMesh":
            //     const skinnedMesh = obj as SkinnedMesh;
            //     clone = new (Object.getPrototypeOf(obj).constructor)(skinnedMesh.geometry, skinnedMesh.material);
            //     break;

            default:
                try {
                    return obj.clone() as ObjectWithMaterial;
                }
                catch (error) {
                    logger.debug(`Object3D.clone() failed for type ${obj.type} (${obj.name || "unnamed"})`, error);
                    return null;
                }
        }
    }
    catch (error) {
        logger.debug(`Could not clone object of type ${obj.type} (${obj.name || "unnamed"})`, error);
        return null;
    }

    if (!clone) {
        throw new Error(`Could not clone object of type ${obj.type}`);
    }

    clone.name = obj.name;
    clone.position.copy(obj.position);
    clone.quaternion.copy(obj.quaternion);
    clone.scale.copy(obj.scale);
    clone.matrix.copy(obj.matrix);
    clone.matrixWorld.copy(obj.matrixWorld);
    clone.layers.mask = obj.layers.mask;
    clone.uuid = Date.now().toString() + Math.random().toString();
    clone.renderOrder = obj.renderOrder + 1;

    return clone;
}



export function createHelperObjectIfNecessary(obj: Object3D, options: {
    camera?: Camera | null,
    htmlElement?: HTMLElement | null,
    allowAxesHelper?: boolean

}): Array<Object3D | Controls<any>> | null {
    if (isLight(obj)) {
        switch (obj.type) {
            case "DirectionalLight":
                {
                    const light = obj as DirectionalLight;
                    const helper = new DirectionalLightHelper(light, 1);
                    return [helper];
                }
            case "PointLight":
                {
                    const light = obj as PointLight;
                    const helper = new PointLightHelper(light, light.distance || 1);
                    return [helper];
                }
            case "SpotLight":
                {
                    const helper = new SpotLightHelper(obj as Light);
                    return [helper];
                }
        }
    }
    else if (isCamera(obj)) {
        if (obj.coordinateSystem === undefined) {
            console.warn("Camera has no coordinate system defined, cannot create helper.");
            return null;
        }
        // TODO: somehow the camera helper breaks in chrome extension (sometimes?) e.g. here https://engine.needle.tools/samples-uploads/physics-animation/
        const helper = new CameraHelper(obj as Camera);
        /** @ts-expect-error */
        helper.update = undefined;
        return [helper];
    }
    else if (isSkinnedMeshLike(obj)) {
        const skinnedMesh = obj as SkinnedMesh;
        const skeletonHelper = new SkeletonHelper(skinnedMesh);
        return [skeletonHelper];
    }


    // TODO: importing TransformControls here causes issues with three.js builds that do not include it
    // if (options.camera && options.htmlElement) {
    //     const transformControls = new TransformControls(options.camera, options.htmlElement);
    //     transformControls.attach(obj);
    //     transformControls.setSize(1.2);
    //     return [transformControls];
    // }

    return null;
}


// #region Raycast


const raycasterVec2 = new Vector2();
export function getRaycaster(evt: PointerEvent | MouseEvent | { x: number, y: number }, camera: Camera, htmlElement: HTMLElement): Raycaster {
    const rect = htmlElement.getBoundingClientRect();
    let x = 0;
    let y = 0;
    if ('clientX' in evt && 'clientY' in evt) {
        x = evt.clientX;
        y = evt.clientY;
    }
    else {
        x = evt.x;
        y = evt.y;
    }

    const nx = ((x - rect.left) / rect.width) * 2 - 1;
    const ny = -((y - rect.top) / rect.height) * 2 + 1;
    const rc = new Raycaster();
    // three's default Line threshold is a 1-UNIT hit radius — with real line
    // content in the scene (the grid) that magnetizes every nearby click
    rc.params.Line.threshold = 0.02;
    raycasterVec2.set(nx, ny);
    rc.setFromCamera(raycasterVec2, camera);
    rc.layers = camera.layers;
    if (isPerspectiveCamera(camera)) {
        rc.near = camera.near;
        rc.far = camera.far;
    } else {
        rc.near = 0;
        rc.far = 100_000;
    }
    return rc;
}

const _raycastSeenObject = new Set<object>();

// export function raycast(clientX:number, clientY:numbr)

export function raycast(raycaster: Raycaster, objects: Object3D[], results: Intersection[] = [], sort?: boolean): Intersection[] {

    results.length = 0;
    if (!objects || objects.length === 0) {
        return results;
    }

    for (const obj of objects) {
        _recursiveRaycast(obj, results, _raycastSeenObject);
    }

    _raycastSeenObject.clear();
    if (sort) {
        results.sort((a, b) => a.distance - b.distance);
    }
    return results;


    function _recursiveRaycast(obj: Object3D, results: Intersection[], seen: Set<object>): void {
        if (!obj) return;
        if (!obj.visible) return;
        if (isHideInHierarchy(obj)) return;

        if (seen.has(obj)) return;
        seen.add(obj);

        // if (isBone(obj)) {
        //     results = raycastBone(obj, raycaster, results);
        // }

        if ("geometry" in obj && obj.geometry) {
            // `obj.raycast(...)` — NOT `raycaster.intersectObject(obj, …)`, which
            // ends with `intersects.sort(ascSort)`. Calling that once per object
            // into this shared, growing array re-sorted it on EVERY object; the
            // single sort below is equivalent (three has one fixed comparator —
            // by distance — and `raycast` only appends, so no object can impose
            // its own order) and, being one stable pass, gives a more predictable
            // tie order than N interleaved sorts.
            if (obj.layers.test(raycaster.layers)) obj.raycast(raycaster, results);
        }

        for (const child of obj.children) {
            _recursiveRaycast(child, results, seen);
        }
    }
}


/** meshes above this vertex count answer depth probes with their bounding box */
export const APPROX_RAYCAST_EXACT_VERTEX_LIMIT = 10_000;

const _approxLocalRay = new Ray();
const _approxInverse = new Matrix4();
const _approxPoint = new Vector3();
const _approxSeen = new Set<object>();

/**
 * Cost-bounded raycast for DEPTH PROBES (orbit rotation center, dolly/pan
 * reference distance): meshes above {@link APPROX_RAYCAST_EXACT_VERTEX_LIMIT}
 * are answered by intersecting their ORIENTED bounding box (the ray transformed
 * into local space — one matrix invert per large mesh) instead of their
 * triangles. Same traversal semantics as {@link raycast} (visibility,
 * HideInHierarchy, layers, single sort).
 *
 * NOT for selection picking: a hit on a large mesh lands on the box surface,
 * not the geometry — fine for "how far away is the thing in front", wrong for
 * "which object did I click". Triangle precision on a scanned mesh cost
 * hundreds of ms PER PROBE (trace 2026-07-30) — this is the budget fix.
 */
export function raycastApproximate(raycaster: Raycaster, objects: Object3D[], results: Intersection[] = [], sort?: boolean): Intersection[] {
    results.length = 0;
    if (!objects || objects.length === 0) {
        return results;
    }
    for (const obj of objects) {
        _recursiveApproxRaycast(obj);
    }
    _approxSeen.clear();
    if (sort) {
        results.sort((a, b) => a.distance - b.distance);
    }
    return results;

    function _recursiveApproxRaycast(obj: Object3D): void {
        if (!obj) return;
        if (!obj.visible) return;
        if (isHideInHierarchy(obj)) return;
        if (_approxSeen.has(obj)) return;
        _approxSeen.add(obj);

        const geometry = (obj as Mesh).geometry;
        if (geometry && obj.layers.test(raycaster.layers)) {
            const vertexCount = (geometry.attributes?.position as { count?: number } | undefined)?.count ?? 0;
            if (vertexCount <= APPROX_RAYCAST_EXACT_VERTEX_LIMIT) {
                obj.raycast(raycaster, results);
            }
            else {
                if (!geometry.boundingBox) geometry.computeBoundingBox(); // cached by three
                const box = geometry.boundingBox;
                if (box && !box.isEmpty()) {
                    _approxInverse.copy(obj.matrixWorld).invert();
                    _approxLocalRay.copy(raycaster.ray).applyMatrix4(_approxInverse);
                    if (_approxLocalRay.intersectBox(box, _approxPoint)) {
                        _approxPoint.applyMatrix4(obj.matrixWorld);
                        const distance = raycaster.ray.origin.distanceTo(_approxPoint);
                        if (distance >= raycaster.near && distance <= raycaster.far) {
                            results.push({ distance, point: _approxPoint.clone(), object: obj } as Intersection);
                        }
                    }
                }
            }
        }

        for (const child of obj.children) {
            _recursiveApproxRaycast(child);
        }
    }
}


const boneMesh = new Mesh(
    new BoxGeometry(1, 1, 1),
    new MeshBasicMaterial({ visible: false })
);


// function raycastBone(bone: Bone, raycaster: Raycaster, target: Intersection[]): Intersection[] {

//     bone.updateWorldMatrix(true, false);
//     boneMesh.position.setFromMatrixPosition(bone.matrixWorld);
//     boneMesh.scale.set(0.1, 0.1, 0.1);
//     boneMesh.updateMatrixWorld(true);

//     console.log("RAYCAST BONE", bone.name, boneMesh.position);
//     return raycaster.intersectObject(boneMesh, false, target);
// }

// #region Fit Camera

/**
 * Where on the canvas the framed object should END UP, as fractions of the
 * canvas. Defaults to the whole canvas (centred), which is the classic behaviour.
 *
 * The editor's canvas spans the window BEHIND the dock panels, so "centre of the
 * canvas" is usually partly hidden. Passing the free viewer area here makes F
 * frame the object into the visible gap — as if that gap were the whole screen.
 */
export interface FitCameraViewport {
    /** free width / canvas width (0..1] */
    fractionX: number;
    /** free height / canvas height (0..1] */
    fractionY: number;
    /** free-area centre in NDC (-1..1, y UP): 0,0 = canvas centre */
    centerNdcX: number;
    centerNdcY: number;
}

/**
 * Whether `Box3.expandByObject(obj, true)` — the PRECISE, per-vertex path — is
 * safe and meaningful for this subtree. Two ways it isn't, both seen in the wild:
 *
 * 1. **Batched / instanced meshes.** three gates the precise path on
 *    `object.isInstancedMesh !== true`, which misses **BatchedMesh** (its own
 *    comment: instancing "isn't supported so far"). For those the vertices sit in
 *    one shared packed buffer in their AUTHORING-local spaces; the per-instance
 *    matrices that actually place them live in `_matricesTexture` and are never
 *    applied — so the box describes somewhere that doesn't exist and F flew off
 *    to nowhere (messenger.abeto.co, 588 instances). The conservative path uses
 *    the object-level `boundingBox`, which DOES fold those matrices in.
 * 2. **A host three older than ours.** The precise path calls
 *    `object.getVertexPosition(i, …)` for anything with `isMesh === true`; that
 *    method arrived ~r151, so an older host mesh throws
 *    "getVertexPosition is not a function" (next.junni.co.jp). helper.ts already
 *    shims the opposite direction (host NEWER than us — see determinantAffine).
 *
 * Bonus: the conservative path is O(objects) instead of O(vertices), so focus no
 * longer walks 300k vertices on a keypress. The looser box is hidden by the 15%
 * padding fitCamera already applies.
 */
function canUsePreciseBounds(root: Object3D): boolean {
    let precise = true;
    root.traverse((child) => {
        if (!precise) return;
        const c = child as Object3D & { isMesh?: boolean; boundingBox?: unknown; getVertexPosition?: unknown };
        // exposes object-level bounds → batched/instanced; conservative is CORRECT
        if (c.boundingBox !== undefined) precise = false;
        // host mesh predating getVertexPosition → precise would throw
        else if (c.isMesh === true && typeof c.getVertexPosition !== "function") precise = false;
    });
    return precise;
}

export function fitCamera(camera: Camera, object: Object3D[] | Vector3[], viewport?: FitCameraViewport): null | { newPosition: Vector3, newRotation: Quaternion, focusPoint: Vector3, fov?: number, near?: number, far?: number } {

    if (!isPerspectiveCamera(camera)) {
        return null;
    }


    const box = new Box3();
    const points = object.filter((item): item is Vector3 => (item as Vector3).isVector3 === true);
    for (const obj of object.filter((item): item is Object3D => (item as Vector3).isVector3 !== true)) {
        // `precise: false` on purpose — see canUsePreciseBounds(). The precise
        // path walks every vertex, which is both WRONG for batched/instanced
        // meshes and unsafe against a host three older than ours.
        box.expandByObject(obj, canUsePreciseBounds(obj));
    }
    for (const point of points) box.expandByPoint(point);
    if (box.isEmpty()) {
        // A zero-extent selection — an empty Object3D/Group, a light, a camera,
        // a bone. There is nothing to FRAME, but focus should still AIM at it;
        // returning null here meant F did nothing whatsoever, not even turn.
        const point = new Vector3();
        for (const obj of object) point.add((obj as Vector3).isVector3 === true ? obj as Vector3 : (obj as Object3D).getWorldPosition(new Vector3()));
        if (object.length === 0) return null;
        point.divideScalar(object.length);

        const position = camera.getWorldPosition(new Vector3());
        // Keep the current distance. With no bounds there is no scale to derive a
        // framing distance from, and teleporting to an invented one is worse than
        // turning to look — the object gives us direction, not size.
        const toPoint = point.clone().sub(position);
        if (toPoint.lengthSq() < 1e-12) return null; // camera sits ON it: no direction to face
        const lookAt = new Matrix4().lookAt(position, point, camera.up);
        return {
            newPosition: position,
            newRotation: new Quaternion().setFromRotationMatrix(lookAt),
            focusPoint: point,
        };
    }

    const center = box.getCenter(new Vector3());
    const rotation = camera.getWorldQuaternion(new Quaternion());
    const inverseRotation = rotation.clone().invert();
    const forward = new Vector3(0, 0, -1).applyQuaternion(rotation).normalize();

    const corners = getBoxCorners(box);
    let maxX = 0;
    let maxY = 0;
    let maxZ = 0;
    // keep the camera-local corners: an OFF-CENTRE fit has to re-check them (see
    // the refinement below), not just their symmetric extents
    const localCorners: Vector3[] = [];
    for (const corner of corners) {
        const localCorner = corner.sub(center).applyQuaternion(inverseRotation);
        localCorners.push(localCorner);
        maxX = Math.max(maxX, Math.abs(localCorner.x));
        maxY = Math.max(maxY, Math.abs(localCorner.y));
        maxZ = Math.max(maxZ, Math.abs(localCorner.z));
    }

    const verticalFov = Math.PI * camera.getEffectiveFOV() / 180;
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * camera.aspect);
    const tanV = Math.tan(verticalFov / 2);
    const tanH = Math.tan(horizontalFov / 2);

    // Fit into the FREE area rather than the whole canvas: a gap that is e.g. half
    // the canvas wide subtends half the angle, so the same object needs twice the
    // distance to fit inside it. Guarded against a degenerate/zero fraction.
    const clamp01Fraction = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0.05, n)) : 1);
    const fractionX = clamp01Fraction(viewport?.fractionX ?? 1);
    const fractionY = clamp01Fraction(viewport?.fractionY ?? 1);
    const distanceY = maxY / (tanV * fractionY);
    const distanceX = maxX / (tanH * fractionX);
    let distance = Math.max(distanceX, distanceY) * 1.15 + maxZ;

    // Landing the object OFF-CENTRE (in the free area's middle) is done by sliding
    // the camera sideways — orientation is untouched, so rendering needs no
    // off-axis frustum and focusPoint stays the object centre. But sliding makes
    // the object oblique to the view axis, and near corners then project FURTHER
    // out than the on-axis estimate above predicts, so the analytic distance can
    // leave the object poking out of the free area. Re-check the real corners and
    // grow the distance until they all fit.
    //
    // With the camera slid to put the centre at (cx, cy), a local corner
    // (lx, ly, lz) projects to:
    //     ndcX = lx / ((d - lz) * tanH) + cx * d / (d - lz)
    // i.e. the on-axis term plus an obliqueness term that vanishes at cx = 0.
    const ndcX = viewport?.centerNdcX ?? 0;
    const ndcY = viewport?.centerNdcY ?? 0;
    if (ndcX !== 0 || ndcY !== 0) {
        const fits = (d: number): boolean => {
            for (const c of localCorners) {
                const depth = d - c.z;
                if (depth <= 1e-6) return false; // corner at/behind the camera
                const x = c.x / (depth * tanH) + ndcX * d / depth;
                const y = c.y / (depth * tanV) + ndcY * d / depth;
                if (Math.abs(x - ndcX) > fractionX || Math.abs(y - ndcY) > fractionY) return false;
            }
            return true;
        };
        // grow to a distance that definitely fits, then bisect back for tightness
        // (monotonic: projected size shrinks as d grows), capped so a pathological
        // box can't spin here.
        let hi = distance;
        for (let i = 0; i < 40 && !fits(hi); i++) hi *= 1.25;
        if (fits(hi)) {
            let lo = distance;
            for (let i = 0; i < 24; i++) {
                const mid = (lo + hi) / 2;
                if (fits(mid)) hi = mid; else lo = mid;
            }
            distance = hi;
        }
    }

    // FOV fitting alone can put tiny or flat bounds in front of the near plane.
    // Keep the nearest corner beyond it, with a small precision margin.
    distance = Math.max(distance, maxZ + camera.near * 1.05);

    const position = center.clone().addScaledVector(forward, -distance);
    if (ndcX !== 0 || ndcY !== 0) {
        const right = new Vector3(1, 0, 0).applyQuaternion(rotation).normalize();
        const up = new Vector3(0, 1, 0).applyQuaternion(rotation).normalize();
        position.addScaledVector(right, -ndcX * tanH * distance);
        position.addScaledVector(up, -ndcY * tanV * distance);
    }

    return {
        newPosition: position,
        newRotation: rotation,
        focusPoint: center.clone(),
    };
}

/** Free viewer area → {@link FitCameraViewport}. `free` must be inside `canvas`
 *  (both in the same coordinate space — viewport pixels). */
export function fitViewportFromRects(
    canvas: { left: number; top: number; width: number; height: number },
    free: { left: number; top: number; width: number; height: number },
): FitCameraViewport {
    const canvasCx = canvas.left + canvas.width / 2;
    const canvasCy = canvas.top + canvas.height / 2;
    const freeCx = free.left + free.width / 2;
    const freeCy = free.top + free.height / 2;
    // `+ 0` normalises -0 (a centred free area negates to -0, which is surprising
    // to callers and to equality checks)
    return {
        fractionX: canvas.width > 0 ? free.width / canvas.width : 1,
        fractionY: canvas.height > 0 ? free.height / canvas.height : 1,
        // screen y grows DOWN, NDC y grows UP → negate the vertical offset
        centerNdcX: canvas.width > 0 ? (freeCx - canvasCx) / (canvas.width / 2) + 0 : 0,
        centerNdcY: canvas.height > 0 ? -(freeCy - canvasCy) / (canvas.height / 2) + 0 : 0,
    };
}

function getBoxCorners(box: Box3): Vector3[] {
    const { min, max } = box;
    return [
        new Vector3(min.x, min.y, min.z),
        new Vector3(min.x, min.y, max.z),
        new Vector3(min.x, max.y, min.z),
        new Vector3(min.x, max.y, max.z),
        new Vector3(max.x, min.y, min.z),
        new Vector3(max.x, min.y, max.z),
        new Vector3(max.x, max.y, min.z),
        new Vector3(max.x, max.y, max.z),
    ];
}


// #region Blink
interface BlinkOptions {
    repeat?: number;
    intervalMs?: number;
}
export async function blink(object: Object3D, options?: BlinkOptions, logger?: ILogService): Promise<void> {

    const repeat = options?.repeat || 3;
    const intervalMs = options?.intervalMs || 200;
    const startVisible = object.visible;
    for (let i = 0; i < repeat; i++) {
        try {
            object.visible = !startVisible;
            await new Promise<void>((resolve) => setTimeout(() => resolve(), intervalMs));
            object.visible = startVisible;
            await new Promise<void>((resolve) => setTimeout(() => resolve(), intervalMs));
        }
        catch (err) {
            logger?.error("Error during blink operation", err);
        }
    }
    object.visible = startVisible;
}


// #region GLTFLoader
export let ktx2Loader: KTX2Loader | undefined;
try {
    ktx2Loader = new KTX2Loader();
    const ktxSupportRenderer = new WebGLRenderer();
    try {
        ktx2Loader.detectSupport(ktxSupportRenderer);
    }
    finally {
        ktxSupportRenderer.dispose();
        ktxSupportRenderer.forceContextLoss();
    }
    ktx2Loader.setTranscoderPath(getBasisTranscoderPath());
} catch { /* non-fatal: agent/headless environments stub THREE */ }

export let dracoLoader: DRACOLoader | undefined;
try {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
} catch { /* non-fatal: agent/headless environments stub THREE */ }

type LoadGLTFOptions = {
    resourcePath?: string,
    resolveUrl?: LoadingManager["resolveURL"]
}

/**
 * Load a NON-glTF model format from bytes for PREVIEW purposes (thumbnails) —
 * the same formats the engine's loader dispatch knows (engine_loaders.ts
 * onCreateLoader: fbx/obj/usdz), via the three example loaders. Lazily
 * imported so the loaders stay out of the main (extension) bundle. Returns
 * the root Object3D, or null for unsupported extensions / parse failures.
 */
export async function loadModelForPreview(data: ArrayBuffer, extension: string): Promise<Object3D | null> {
    try {
        switch (extension.toLowerCase()) {
            case "fbx": {
                const { FBXLoader } = await import("three/examples/jsm/loaders/FBXLoader.js");
                return new FBXLoader().parse(data, "");
            }
            case "obj": {
                const { OBJLoader } = await import("three/examples/jsm/loaders/OBJLoader.js");
                return new OBJLoader().parse(new TextDecoder().decode(data));
            }
            case "usdz":
            case "usd":
            case "usda":
            case "usdc": {
                const { USDLoader } = await import("three/examples/jsm/loaders/USDLoader.js");
                return await new Promise<Object3D | null>((resolve) => {
                    (new USDLoader().parse as (buffer: ArrayBuffer, path: string, onLoad: (root: Object3D) => void, onError: (error: unknown) => void) => void)(
                        data, "", root => resolve(root), () => resolve(null));
                });
            }
            default:
                return null;
        }
    }
    catch {
        // a parse failure means "no thumbnail", never a broken tile
        return null;
    }
}

export async function loadGLTF(data: ArrayBuffer | URI, token?: CancellationToken, options?: LoadGLTFOptions): Promise<GLTFLike<Scene> | null> {

    let resourcePath = options?.resourcePath || undefined;

    const gltfLoader = new GLTFLoader();
    gltfLoader.resourcePath = options?.resourcePath || resourcePath || '';
    if (dracoLoader) gltfLoader.setDRACOLoader(dracoLoader);
    if (ktx2Loader) gltfLoader.setKTX2Loader(ktx2Loader);
    gltfLoader.setMeshoptDecoder(MeshoptDecoder);

    if (options?.resolveUrl) gltfLoader.manager.resolveURL = options.resolveUrl;

    // gltfLoader.register(_parser => {
    //     return {
    //         name: 'NeedleEngineExtension',
    //         async afterRoot(gltf: GLTF) {
    //             console.log("LOADED GLTF", gltf);
    //         }
    //     }
    // })

    // let baseUrl = '';
    // if (data instanceof URI) {
    //     baseUrl = data.toString().substring(0, data.toString().lastIndexOf('/') + 1);
    // }
    // else options?.resolveUrl = url => {
    //     if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    //         return url;
    //     }
    //     return baseUrl + url;
    // }

    try {
        const gltf = data instanceof URI
            ? await gltfLoader.loadAsync(data.toString())
            : await gltfLoader.parseAsync(data, '');

        // if (ext?.gltf)
        //     getLoader().createBuiltinComponents(Context.Current, "", gltf, null, ext);


        return gltf as unknown as GLTFLike<Scene>;

    } catch (error) {
        console.error("Failed to load GLTF:", error);
        return null;
    }
}


/** Tone-map float RGBA pixels (HDRLoader.parse output) to LDR bytes for a
 * thumbnail: Reinhard (c/(1+c)) + gamma 1/2.2, nearest-sampled to the target
 * size, alpha forced opaque. Pure CPU — usable without any GL context. */
export function tonemapHdrToRgba8(source: Float32Array, sourceWidth: number, sourceHeight: number, targetWidth: number, targetHeight: number): Uint8ClampedArray {
    const out = new Uint8ClampedArray(targetWidth * targetHeight * 4);
    const ldr = (v: number) => Math.round(Math.min(1, Math.pow(v / (1 + v), 1 / 2.2)) * 255);
    for (let y = 0; y < targetHeight; y++) {
        const sy = Math.min(sourceHeight - 1, Math.floor(y * sourceHeight / targetHeight));
        for (let x = 0; x < targetWidth; x++) {
            const sx = Math.min(sourceWidth - 1, Math.floor(x * sourceWidth / targetWidth));
            const si = (sy * sourceWidth + sx) * 4;
            const di = (y * targetWidth + x) * 4;
            out[di] = ldr(source[si]);
            out[di + 1] = ldr(source[si + 1]);
            out[di + 2] = ldr(source[si + 2]);
            out[di + 3] = 255;
        }
    }
    return out;
}

/** which three loader a texture url needs, by extension — callers that load
 * through a blob url derive this from the ORIGINAL name (a blob url has none) */
export function textureTypeFromUrl(url: string): "hdr" | "exr" | "ktx2" | "texture" {
    const urlLower = url.toLowerCase();
    if (urlLower.endsWith(".hdr")) return "hdr";
    if (urlLower.endsWith(".exr")) return "exr";
    if (urlLower.endsWith(".ktx2")) return "ktx2";
    return "texture";
}

export async function loadTexture(_url: string | URI, type?: "hdr" | "exr" | "ktx2" | "texture"): Promise<Texture | null> {
    let loader: Loader<Texture | null> | null = null;

    const url = typeof _url === "string" ? _url : _url.toString();

    if (!type) {
        type = textureTypeFromUrl(url);
    }

    if (type === "hdr") {
        await import("three/examples/jsm/loaders/HDRLoader.js").then(({ HDRLoader }) => {
            loader = new HDRLoader();
        });
    }
    else if (type === "exr") {
        loader = new EXRLoader();
    }
    else if (type === "ktx2") {
        const ktx2Loader = new KTX2Loader();
        ktx2Loader.setTranscoderPath(getBasisTranscoderPath());
        loader = ktx2Loader;
    }
    else if (type === "texture") {
        loader = new TextureLoader();
    }

    if (!loader) {
        console.warn(`No loader available for texture type: ${type}`);
        return null;
    }

    return loader.loadAsync(url);
}
